import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  CreateInvestorGroupRequest,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  DeleteInvestorGroupResult,
  InvestorGroup,
  InvestorGroupApiItem,
  InvestorGroupDocumentItem,
  InvestorGroupNoteItem,
  NoteUpsertRequest,
  UpdateInvestorGroupRequest,
} from './investor-group.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class InvestorGroupService {
  private readonly endpoint = apiUrl('investorgroup');
  private readonly documentsEndpoint = apiUrl('document');
  private readonly notesEndpoint = apiUrl('Notes');

  constructor(private readonly http: HttpClient) {}

  getInvestorGroups(): Observable<InvestorGroup[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeInvestorGroup(item))),
    );
  }

  createInvestorGroup(payload: CreateInvestorGroupRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateInvestorGroup(id: number, payload: UpdateInvestorGroupRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteInvestorGroup(id: number): Observable<DeleteInvestorGroupResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteInvestorGroupResult => ({ kind: 'success' })),
      catchError(() => of<DeleteInvestorGroupResult>({
        kind: 'error',
        message: 'Unable to delete investor group at this time. Please try again.',
      })),
    );
  }

  getDocumentsByParent(parentElementId: string): Observable<InvestorGroupDocumentItem[]> {
    return this.http.get<unknown>(`${this.documentsEndpoint}/trade/${encodeURIComponent(parentElementId)}`).pipe(
      map((response) => this.normalizeDocuments(response)),
    );
  }

  createDocument(payload: DocumentCreateRequest): Observable<InvestorGroupDocumentItem> {
    const formData = new FormData();
    formData.append('Filename', payload.filename);
    if (payload.parentElementId) {
      formData.append('ParentElementId', payload.parentElementId);
    }
    if (payload.securityId) {
      formData.append('SecurityId', payload.securityId);
    }
    formData.append('Description', payload.description ?? '');
    formData.append('Type', payload.type);
    formData.append('Source', payload.source);
    formData.append('ModifiedBy', payload.modifiedBy);
    formData.append('File', payload.file);

    return this.http.post<unknown>(this.documentsEndpoint, formData).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  updateDocument(id: number, payload: DocumentUpdateRequest): Observable<InvestorGroupDocumentItem> {
    return this.http.put<unknown>(`${this.documentsEndpoint}/${encodeURIComponent(String(id))}`, payload).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.documentsEndpoint}/${encodeURIComponent(String(id))}`);
  }

  getNotes(): Observable<InvestorGroupNoteItem[]> {
    return this.http.get<unknown>(this.notesEndpoint).pipe(
      map((response) => this.normalizeNotes(response)),
    );
  }

  createNote(payload: NoteUpsertRequest): Observable<InvestorGroupNoteItem> {
    return this.http.post<unknown>(this.notesEndpoint, payload).pipe(
      map((response) => this.normalizeNote(response)),
    );
  }

  updateNote(id: number, payload: NoteUpsertRequest): Observable<void> {
    return this.http.put<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`, payload);
  }

  deleteNote(id: number): Observable<void> {
    return this.http.delete<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`);
  }

  private normalizeInvestorGroup(item: InvestorGroupApiItem): InvestorGroup {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      hqCountryId: this.toNullableNumber(item.hqCountryId),
      country: this.toText(item.country),
      regionId: this.toNullableNumber(item.regionId),
      region: this.toText(item.region),
      createdBy: this.toText(item.createdBy),
      dateCreated: this.toText(item.dateCreated),
      modifiedBy: this.toText(item.modifiedBy),
      dateModified: this.toText(item.dateModified),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: InvestorGroupApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private normalizeDocuments(response: unknown): InvestorGroupDocumentItem[] {
    if (Array.isArray(response)) {
      return response.map((entry) => this.toDocument(this.toRecord(entry)));
    }

    const record = this.toRecord(response);
    const data = record['data'];
    if (Array.isArray(data)) {
      return data.map((entry) => this.toDocument(this.toRecord(entry)));
    }

    return [];
  }

  private toDocument(record: Record<string, unknown>): InvestorGroupDocumentItem {
    return {
      id: this.toNumber(record['id'] ?? record['Id'] ?? record['documentId'] ?? record['DocumentId']),
      parentElementId: this.toText(record['parentElementId'] ?? record['ParentElementId']),
      securityId: this.toText(record['securityId'] ?? record['SecurityId']),
      description: this.toText(record['description'] ?? record['Description'] ?? record['name'] ?? record['Name']),
      type: this.toText(record['type'] ?? record['Type'] ?? record['documentType'] ?? record['DocumentType']),
      filename: this.toText(record['filename'] ?? record['Filename'] ?? record['fileName'] ?? record['FileName']),
      ezShareId: this.toText(record['ezShareId'] ?? record['EzShareId'] ?? record['eZShareId'] ?? record['EZShareId']),
      ezShareLink: this.toText(record['ezShareLink'] ?? record['EzShareLink'] ?? record['eZShareLink'] ?? record['EZShareLink']),
      source: this.toText(record['source'] ?? record['Source']),
    };
  }

  private normalizeNotes(response: unknown): InvestorGroupNoteItem[] {
    if (Array.isArray(response)) {
      return response.map((entry) => this.toNote(this.toRecord(entry)));
    }

    const record = this.toRecord(response);
    const data = record['data'];
    if (Array.isArray(data)) {
      return data.map((entry) => this.toNote(this.toRecord(entry)));
    }

    return [];
  }

  private normalizeNote(response: unknown): InvestorGroupNoteItem {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);
    return Object.keys(data).length > 0
      ? this.toNote(data)
      : this.toNote(record);
  }

  private toNote(record: Record<string, unknown>): InvestorGroupNoteItem {
    return {
      id: this.toNumber(record['id'] ?? record['Id']),
      parentId: this.toText(record['parentId'] ?? record['ParentId']),
      source: this.toText(record['source'] ?? record['Source']),
      description: this.toText(record['description'] ?? record['Description']),
      modifiedBy: this.toText(record['modifiedBy'] ?? record['ModifiedBy']),
      dateModified: this.toText(record['dateModified'] ?? record['DateModified']),
    };
  }

  private extractResponseRecord(response: unknown): Record<string, unknown> {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);

    return Object.keys(data).length > 0 ? data : record;
  }

  private extractRows(response: unknown): InvestorGroupApiItem[] {
    if (Array.isArray(response)) {
      return response as InvestorGroupApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as InvestorGroupApiItem[];
      }

      if (Array.isArray(items)) {
        return items as InvestorGroupApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  }
}
